import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useAuthStore } from '@shared/store/authStore';
import { useNotifications } from '../../hooks/useNotifications';
import { SMSAuthModal } from '../auth/SMSAuthModal';
import { DesktopSidebar } from './DesktopSidebar';
import { useThemeStore } from '../../store/themeStore';

export function TopBar() {
  const { user, isAuthenticated } = useAuthStore();
  const { unreadCount } = useNotifications();
  const isDark = useThemeStore((state) => state.isDark);
  const location = useLocation();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  // Check if we're on restaurant page
  const isRestaurantPage = location.pathname === '/restaurant';

  // Get cart data from localStorage for restaurant
  const getCartCount = () => {
    if (!isRestaurantPage) return 0;
    try {
      const cartData = localStorage.getItem('restaurant-cart');
      if (cartData) {
        const cart = JSON.parse(cartData);
        return Object.values(cart).reduce((sum: number, val: any) => sum + (typeof val === 'number' ? val : val.quantity || 0), 0);
      }
    } catch (error) {
    }
    return 0;
  };

  // Update cart count when component mounts or location changes
  useEffect(() => {
    const updateCartCount = () => {
      setCartCount(getCartCount());
    };

    updateCartCount();

    // Listen for cart updates
    const handleCartUpdate = () => {
      updateCartCount();
    };

    // Listen for auth modal open requests
    const handleOpenAuthModal = () => {
      setShowAuthModal(true);
    };

    // Listen for storage changes
    window.addEventListener('storage', handleCartUpdate);
    // Listen for custom cart update events
    window.addEventListener('restaurantCartUpdated', handleCartUpdate);
    // Listen for auth modal open requests
    window.addEventListener('openAuthModal', handleOpenAuthModal);

    return () => {
      window.removeEventListener('storage', handleCartUpdate);
      window.removeEventListener('restaurantCartUpdated', handleCartUpdate);
      window.removeEventListener('openAuthModal', handleOpenAuthModal);
    };
  }, [location.pathname]);

  const handleCartClick = () => {
    if (isRestaurantPage) {
      // Trigger cart modal via custom event
      window.dispatchEvent(new CustomEvent('openRestaurantCart'));
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[100] theme-topbar">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Menu button */}
            <div className="flex items-center">
              {/* Menu button for sidebar */}
              <button
                onClick={() => setShowSidebar(true)}
                className="p-3 theme-topbar-button transition-colors"
              >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>

            {/* Center Logo */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <img
                src="/logotype-white.png"
                alt="Level Sport Club"
                className="h-12 w-auto"
                style={{ display: isDark ? 'block' : 'none' }}
              />
              <img
                src="/logotype-dark.png"
                alt="Level Sport Club"
                className="h-12 w-auto"
                style={{ display: isDark ? 'none' : 'block' }}
              />
            </div>

            {/* Right side - cart and notifications */}
            <div className="flex items-center">
              {/* Cart - only show on restaurant page */}
              {isRestaurantPage && (
                <button
                  onClick={handleCartClick}
                  className="relative p-3 theme-topbar-button transition-colors"
                  title="Корзина"
                >
                  <ShoppingCart className="w-7 h-7" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount > 9 ? '9+' : cartCount}
                    </span>
                  )}
                </button>
              )}

              {/* Notifications */}
              <button
                onClick={() => navigate('/notifications')}
                className="relative p-3 theme-topbar-button transition-colors"
              >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* SMS Authentication Modal */}
      {showAuthModal && (
        <SMSAuthModal onClose={() => setShowAuthModal(false)} />
      )}

      {/* Desktop Sidebar */}
      <DesktopSidebar
        isOpen={showSidebar}
        onClose={() => setShowSidebar(false)}
        onLoginClick={() => setShowAuthModal(true)}
      />

      {/* Overlay to close user menu */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </>
  );
}